<template>
  <div>
    <div class="d-flex flex-column-fluid">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <!-- Header Section -->
            <div class="row">
              <div class="col-lg-12 col-xl-12">
                <div class="card card-custom gutter-b bg-transparent shadow-none border-0">
                  <div class="card-header align-items-center border-bottom-dark px-0">
                    <div class="card-title mb-0">
                      <h3 class="card-label mb-0 font-weight-bold text-body">
                        Brand Banner Setting
                      </h3>
                    </div>
                    <div class="icons d-flex">
                      <button
                        v-if="$parent.permissions.includes('brandBanner-banner-manage')"
                        class="btn ml-2 p-0 kt_notes_panel_toggle"
                        @click="display_form = !display_form"
                        aria-label="Add new brand banner"
                      >
                        <span class="bg-secondary h-30px font-size-h5 w-30px d-flex align-items-center justify-content-center rounded-circle shadow-sm">
                          <svg width="25px" height="25px" viewBox="0 0 16 16" class="bi bi-plus white" fill="currentColor">
                            <path fill-rule="evenodd" d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Section -->
            <div class="row">
              <div class="col-12">
                <div class="card card-custom gutter-b bg-white border-0">
                  <div class="card-body">
                    <div class="table-container">
                      <div class="table-controls mb-3">
                        <div class="row align-items-center">
                          <div class="col-md-6">
                            <label class="d-flex align-items-center">
                              Show
                              <select
                                v-model="limit"
                                @change="fetchBrandBanners()"
                                class="form-control form-control-sm mx-2"
                                aria-label="Items per page"
                              >
                                <option v-for="option in pageSizeOptions" :value="option">{{ option }}</option>
                              </select>
                              entries
                            </label>
                          </div>
                          <div class="col-md-6 text-right">
                            <div class="search-box">
                              <label class="d-flex align-items-center justify-content-end">
                                Search:
                                <input
                                  type="text"
                                  v-model="searchParameter"
                                  @input="debounceSearch"
                                  class="form-control form-control-sm ml-2"
                                  placeholder="Search..."
                                  aria-label="Search brand banners"
                                />
                                <button
                                  v-if="searchParameter"
                                  @click="clearSearch"
                                  class="btn btn-sm btn-link ml-2"
                                  aria-label="Clear search"
                                >
                                  Clear
                                </button>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Data Table -->
                      <div class="table-responsive">
                        <table class="table table-hover">
                          <thead class="text-body">
                            <tr>
                              <th @click="sorting('id')" :class="getSortClass('id')">ID</th>
                              <th @click="sorting('title')" :class="getSortClass('title')">Title</th>
                              <th>Brand</th>
                              <th @click="sorting('type')" :class="getSortClass('type')">Type</th>
                              <th @click="sorting('discount_type')" :class="getSortClass('discount_type')">Discount Type</th>
                              <th @click="sorting('discount')" :class="getSortClass('discount')">Discount</th>
                              <th @click="sorting('price_less')" :class="getSortClass('price_less')">Price Less</th>
                              <th @click="sorting('status')" :class="getSortClass('status')">Status</th>
                              <th>Image</th>
                              <th v-if="$parent.permissions.includes('brandBanner-banner-manage')">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-if="loadingData">
                              <td colspan="8" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                  <span class="sr-only">Loading...</span>
                                </div>
                              </td>
                            </tr>
                            <tr v-else-if="brandBanners.length === 0">
                              <td colspan="8" class="text-center py-4">No data available</td>
                            </tr>
                            <tr v-for="brandBanner in brandBanners" :key="brandBanner.id">
                              <td>{{ brandBanner.id }}</td>
                              <td>{{ brandBanner.title }}</td>
                              <td>
                                <span v-if="brandBanner.brand" class="text-primary">
                                  {{ brandBanner.brand.brand_name }}
                                </span>
                                <span v-else class="text-muted">No brand</span>
                              </td>
                         
                              <td>
                                <span v-if="brandBanner.type === 0" class="badge badge-primary">Banner</span>
                                <span v-else class="badge badge-success">Brand Discount</span>
                              </td>
                              <td>
                                <span :class="`badge badge-${brandBanner.status ? 'success' : 'danger'}`">
                                  {{ brandBanner.discount_type ==1 ? 'Percentage' : 'Fixed' }}
                                </span>
                              </td>
                              <td>{{ brandBanner.discount }}</td>
                              <td>{{ brandBanner.price_less }}</td>
                              <td>
                                <span :class="`badge badge-${brandBanner.status ? 'success' : 'danger'}`">
                                  {{ brandBanner.status ? 'Active' : 'Inactive' }}
                                </span>
                              </td>
                              <td>
                                <img
                                  v-if="brandBanner.gallary"
                                  class="img-thumbnail"
                                  :src="getImagePath(brandBanner.gallary.gallary_name)"
                                  :alt="brandBanner.title"
                                  width="80"
                                  height="60"
                                  @error="handleImageError"
                                />
                                <span v-else class="text-muted">No image</span>
                              </td>
                              <td v-if="$parent.permissions.includes('brandBanner-banner-manage')">
                                <button 
                                  @click="editBrandBanner(brandBanner)"
                                  class="btn btn-icon btn-sm btn-primary mr-2"
                                  aria-label="Edit"
                                >
                                  <i class="fas fa-edit"></i>
                                </button>
                                <button
                                  @click="deleteBrandBanner(brandBanner.id)"
                                  class="btn btn-icon btn-sm btn-danger"
                                  aria-label="Delete"
                                >
                                  <i class="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pagination -->
                      <div class="pagination-container mt-3">
                        <nav aria-label="Page navigation">
                          <ul class="pagination justify-content-center">
                            <li class="page-item" :class="{ disabled: !pagination.prev_page_url }">
                              <button
                                class="page-link"
                                @click="fetchBrandBanners(pagination.prev_page_url)"
                                :disabled="!pagination.prev_page_url"
                                aria-label="Previous page"
                              >
                                Previous
                              </button>
                            </li>
                            <li class="page-item disabled">
                              <span class="page-link">
                                Page {{ pagination.current_page }} of {{ pagination.last_page }}
                              </span>
                            </li>
                            <li class="page-item" :class="{ disabled: !pagination.next_page_url }">
                              <button
                                class="page-link"
                                @click="fetchBrandBanners(pagination.next_page_url)"
                                :disabled="!pagination.next_page_url"
                                aria-label="Next page"
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Offcanvas -->
    <div
      class="offcanvas offcanvas-right kt-color-panel p-5"
      :class="{ 'offcanvas-on': display_form }"
      v-if="display_form"
    >
      <div class="offcanvas-header d-flex align-items-center justify-content-between pb-3">
        <h4 class="font-size-h4 font-weight-bold m-0">
          {{ edit ? 'Edit' : 'Add' }} Brand Banner
        </h4>
        <button
          @click="clearForm()"
          class="btn btn-sm btn-icon btn-light btn-hover-primary"
          aria-label="Close form"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="addUpdateBrandBanner">
        <div class="row">
          <div class="col-12">
            <!-- Basic Information -->
            <div class="form-group">
              <label class="text-dark">Title *</label>
              <input
                v-model="brandBanner.title"
                type="text"
                class="form-control"
                required
              />
              <small class="form-text text-danger" v-if="errors.has('title')">
                {{ errors.get('title') }}
              </small>
            </div>

            <!-- Display Settings -->
            <div class="row">
            
              <div class="col-md-6">
                <div class="form-group">
                  <label class="text-dark">Status *</label>
                  <select v-model="brandBanner.status" class="form-control" required>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                  <small class="form-text text-danger" v-if="errors.has('status')">
                    {{ errors.get('status') }}
                  </small>
                </div>
              </div>
            

              <div class="col-md-6">
                <div class="form-group">
                  <label class="text-dark">Type *</label>
                  <select v-model="brandBanner.type" class="form-control" required>
                    <option value="0">Banner</option>
                    <option value="1">Brand Discount</option>
                  </select>
                  <small class="form-text text-danger" v-if="errors.has('type')">
                    {{ errors.get('type') }}
                  </small>
                </div>
              </div>
</div>
            <!-- Discount and Price Fields -->
            <div class="row">
             

               <div class="col-md-4">
                <div class="form-group">
                  <label class="text-dark">Discount Type *</label>
                  <select v-model="brandBanner.discount_type" class="form-control" required>
                    <option value="1">Percentage</option>
                    <option value="2">Fixed</option>
                  </select>
                  <small class="form-text text-danger" v-if="errors.has('discount_type')">
                    {{ errors.get('discount_type') }}
                  </small>
                </div>
              </div>


              <div class="col-md-4">
                <div class="form-group">
                  <label class="text-dark">Discount</label>
                  <input
                    v-model="brandBanner.discount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    placeholder="Enter discount amount"
                  />
                  <small class="form-text text-danger" v-if="errors.has('discount')">
                    {{ errors.get('discount') }}
                  </small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group">
                  <label class="text-dark">Price Less</label>
                  <input
                    v-model="brandBanner.price_less"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    placeholder="Enter price less amount"
                  />
                  <small class="form-text text-danger" v-if="errors.has('price_less')">
                    {{ errors.get('price_less') }}
                  </small>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="text-dark">Brand *</label>
              <select v-model="brandBanner.brand_id" class="form-control" required>
                <option value="">Select Brand</option>
                <option v-for="brand in brands" :key="brand.brand_id" :value="brand.brand_id">
                  {{ brand.brand_name	 }}
                </option>
              </select>
              <small class="form-text text-danger" v-if="errors.has('brand_id')">
                {{ errors.get('brand_id') }}
              </small>
            </div>

            <!-- Media Section -->
            <div class="form-group">
              <label class="text-dark">Media *</label>
              <div>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="toggleImageSelect"
                >
                  {{ gallary_path ? 'Change Image' : 'Upload Image' }}
                </button>
                <div class="mt-2">
                  <img
                    v-if="gallary_path"
                    :src="cdnUrl(gallary_path)"
                    class="img-thumbnail"
                    width="100"
                    height="100"
                    alt="Selected media"
                    @error="handleImageError"
                  />
                  <small v-else class="text-muted">No image selected</small>
                </div>
                <small class="form-text text-danger" v-if="errors.has('gallary_id')">
                  {{ errors.get('gallary_id') }}
                </small>
              </div>
            </div>

            <!-- Form Actions -->
            <div class="form-group text-right">
              <button
                type="button"
                @click="clearForm"
                class="btn btn-secondary mr-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="processing"
              >
                <span v-if="processing">
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Processing...
                </span>
                <span v-else>
                  {{ edit ? 'Update' : 'Save' }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Image Selector Modal -->
    <attach-image
      v-if="showModal"
      @toggleImageSelect="toggleImageSelect"
      :showModal="showModal"
      @setImage="setImage"
    />
  </div>
</template>

<script>
import ErrorHandling from "../../ErrorHandling";
import { quillEditor } from 'vue-quill-editor';
import debounce from 'lodash/debounce';

export default {
  components: {
    quillEditor
  },
  data() {
    return {
      display_form: false,
      showModal: false,
      edit: false,
      processing: false,
      loadingData: false,
      searchParameter: "",
      sortBy: "id",
      sortType: "ASC",
      limit: 10,
      pageSizeOptions: [10, 25, 50, 100, 200, 500, 1000],
      brandBanner: this.getDefaultBrandBannerData(),
      gallary_path: "",
      brandBanners: [],
      brands: [],
      pages: [],
      errors: new ErrorHandling(),
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link']
          ]
        },
        placeholder: 'Enter description...',
        theme: 'snow'
      },
      debounceSearch: debounce(function() {
        this.fetchBrandBanners();
      }, 500),
      pagination: {}
    };
  },
  created() {
    this.token = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`
      }
    };
    this.fetchInitialData();
  },
  methods: {
    getDefaultBrandBannerData() {
      return {
        id: "",
        title: "",
        type: "",
        discount: "",
        discount_type: "",
        price_less: "",
        status: 1,
        gallary_id: "",
        brand_id: "",
        position: "position-left"
      };
    },
    getImagePath(gallaryName) {
      return gallaryName ? this.cdnUrl(`/gallary/thumbnail${gallaryName}`) : '';
    },
    handleImageError(event) {
     // event.target.style.display = 'none';
    },
    async fetchInitialData() {
      try {
        this.loadingData = true;
        await Promise.all([
          this.fetchBrandBanners(),
          this.fetchBrand(),
          this.fetchPages()
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        this.$toaster.error("Failed to load initial data");
      } finally {
        this.loadingData = false;
      }
    },
    async fetchBrandBanners(page_url) {
      try {
        this.loadingData = true;
        page_url = page_url || "/api/admin/brand_banner";
        const params = {
          limit: this.limit,
          searchParameter: this.searchParameter,
          sortBy: this.sortBy,
          sortType: this.sortType
        };
        
        const response = await axios.get(page_url, { 
          params,
          headers: this.token.headers 
        });
        
        this.brandBanners = response.data.data;
        this.makePagination(response.data.meta, response.data.links);
      } catch (error) {
        console.error("Error fetching brand banners:", error);
        this.$toaster.error("Failed to load brand banners");
      } finally {
        this.loadingData = false;
      }
    },
    makePagination(meta, links) {
      this.pagination = {
        current_page: meta.current_page,
        last_page: meta.last_page,
        next_page_url: links.next,
        prev_page_url: links.prev,
        total: meta.total
      };
    },
    async fetchBrand() {
  try {
    const response = await axios.get("/api/admin/brand?getDetail=1", this.token);
    
    // Add "All Brands" with ID 1000
    this.brands = [
      { brand_id: 1000, brand_name: "All Brands" },
      ...response.data.data
    ];
    
  } catch (error) {
    console.error("Error fetching brands:", error);
    this.$toaster.error("Failed to load brands");
  }
},

    async fetchPages() {
      try {
        const response = await axios.get("/api/admin/pages?limit=20000&getDetail=1", this.token);
        this.pages = response.data.data;
      } catch (error) {
        console.error("Error fetching pages:", error);
        this.$toaster.error("Failed to load pages");
      }
    },
    async addUpdateBrandBanner() {
      this.processing = true;
      this.errors = new ErrorHandling();
      
      try {
        const url = this.edit 
          ? `/api/admin/brand_banner/${this.brandBanner.id}`
          : "/api/admin/brand_banner";
          
        const method = this.edit ? "put" : "post";
        
        const response = await axios[method](url, this.brandBanner, this.token);
        
        if (response.data.status === "Success") {
          this.$toaster.success(`Brand banner ${this.edit ? 'updated' : 'created'} successfully`);
          this.clearForm();
          this.fetchBrandBanners();
        } else {
          this.$toaster.error(response.data.message);
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          if (error.response.data.status === "Error") {
            this.$toaster.error(error.response.data.message);
          } else {
            this.errors.record(error.response.data.errors);
          }
        } else {
          console.error("Error saving brand banner:", error);
          this.$toaster.error("Failed to save brand banner");
        }
      } finally {
        this.processing = false;
      }
    },
    editBrandBanner(brandBanner) {
      this.edit = true;
      this.display_form = true;
      this.errors = new ErrorHandling();
      
      this.brandBanner = {
        ...this.getDefaultBrandBannerData(),
        ...brandBanner,
        status: brandBanner.status.toString()
      };
      
      this.gallary_path = brandBanner.gallary
        ? this.getImagePath(brandBanner.gallary.gallary_name)
        : "";
    },
    confirmDelete(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.deleteBrandBanner(id);
        }
      });
    },
    async deleteBrandBanner(id) {
      try {
        this.loadingData = true;
        const response = await axios.delete(`/api/admin/brand_banner/${id}`, this.token);
        
        if (response.data.status === "Success") {
          this.$toaster.success("Brand banner deleted successfully");
          this.fetchBrandBanners();
        } else {
          this.$toaster.error(response.data.message);
        }
      } catch (error) {
        console.error("Error deleting brand banner:", error);
        this.$toaster.error("Failed to delete brand banner");
      } finally {
        this.loadingData = false;
      }
    },
    clearForm() {
      this.edit = false;
      this.brandBanner = this.getDefaultBrandBannerData();
      this.gallary_path = "";
      this.errors = new ErrorHandling();
      this.display_form = false;
    },
    sorting(sortBy) {
      if (this.sortBy === sortBy) {
        this.sortType = this.sortType === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = sortBy;
        this.sortType = 'asc';
      }
      this.fetchBrandBanners();
    },
    getSortClass(sortBy) {
      return {
        'sorting-asc': this.sortBy === sortBy && this.sortType === 'asc',
        'sorting-desc': this.sortBy === sortBy && this.sortType === 'desc',
        'sortable': true
      };
    },
    toggleImageSelect() {
      this.showModal = !this.showModal;
    },
    setImage(gallary) {
      this.gallary_path = gallary.gallary_path;
      this.brandBanner.gallary_id = gallary.gallary_id;
      this.showModal = false;
    },
    clearSearch() {
      this.searchParameter = "";
      this.fetchBrandBanners();
    }
  }
};
</script>

<style scoped>
.sortable {
  cursor: pointer;
}
.sortable:hover {
  background-color: #f8f9fa;
}
.sorting-asc::after {
  content: " ↑";
}
.sorting-desc::after {
  content: " ↓";
}
.offcanvas {
  width: 600px;
  max-width: 90vw;
}
.img-thumbnail {
  max-width: 100px;
  max-height: 100px;
  object-fit: cover;
}
</style>